import {
  Agriculture,
  Anchor,
  Bolt,
  Brightness2,
  Cake,
  Category,
  CheckCircle,
  Construction,
  DeleteOutline,
  Diamond,
  DirectionsBoat,
  EmojiEvents,
  Event,
  Favorite,
  Flag,
  Forest,
  Grass,
  Handshake,
  Handyman,
  Hardware,
  Home,
  Inventory2,
  Kitchen,
  Label,
  LocalFireDepartment,
  LocalShipping,
  Lock,
  MilitaryTech,
  MonetizationOn,
  Nightlight,
  OutdoorGrill,
  Pets,
  Phone,
  PriorityHigh,
  Restaurant,
  Scale,
  Schedule,
  School,
  Science,
  Spa,
  Star,
  Storefront,
  Thermostat,
  ThumbUp,
  Verified,
  WaterDrop,
  Whatshot,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * The curated icon set a job tag can choose from.
 *
 * Deliberately a fixed registry rather than free-text icon names: the KEY is
 * what lands in Mongo, so the stored value is always one this app can render,
 * and the set stays small enough to show as a swatch grid and to stay legible
 * at 16px on a calendar event.
 *
 * Keys are stable — renaming one orphans every tag already using it. Adding
 * to the set is free; removing from it is not: a removed key needs a migration
 * that moves the tags storing it to a key that still exists, or they fall back
 * to `DEFAULT_JOB_TAG_ICON` and the picker shows nothing selected.
 *
 * Nothing here may repeat a glyph the calendar already gives a meaning of its
 * own: the warning triangle is a job with issues and the snowflake is kill &
 * chill (`components/icons/missing-info-icons`). `warning` and `freezer` were
 * retired for that reason, and API migration
 * `1790990000000-retire-warning-freezer-job-tag-icons` moved the tags using
 * them to `priority` and `temperature`.
 */
export const JOB_TAG_ICONS = {
  label: { label: 'Label', Icon: Label },
  farm: { label: 'Farm', Icon: Agriculture },
  forest: { label: 'Forest', Icon: Forest },
  pets: { label: 'Animal', Icon: Pets },
  grass: { label: 'Grass', Icon: Grass },
  spa: { label: 'Spa', Icon: Spa },
  truck: { label: 'Truck', Icon: LocalShipping },
  boat: { label: 'Boat', Icon: DirectionsBoat },
  anchor: { label: 'Anchor', Icon: Anchor },
  storefront: { label: 'Storefront', Icon: Storefront },
  home: { label: 'Home', Icon: Home },
  phone: { label: 'Call', Icon: Phone },
  rush: { label: 'Rush', Icon: Bolt },
  priority: { label: 'Priority', Icon: PriorityHigh },
  star: { label: 'Star', Icon: Star },
  flag: { label: 'Flag', Icon: Flag },
  medal: { label: 'Medal', Icon: MilitaryTech },
  trophy: { label: 'Trophy', Icon: EmojiEvents },
  diamond: { label: 'Premium', Icon: Diamond },
  verified: { label: 'Verified', Icon: Verified },
  check: { label: 'Check', Icon: CheckCircle },
  thumbUp: { label: 'Thumbs Up', Icon: ThumbUp },
  favorite: { label: 'Heart', Icon: Favorite },
  handshake: { label: 'Handshake', Icon: Handshake },
  temperature: { label: 'Temperature', Icon: Thermostat },
  fridge: { label: 'Fridge', Icon: Kitchen },
  smoke: { label: 'Smoke', Icon: LocalFireDepartment },
  fire: { label: 'Fire', Icon: Whatshot },
  grill: { label: 'Grill', Icon: OutdoorGrill },
  water: { label: 'Water', Icon: WaterDrop },
  scale: { label: 'Scale', Icon: Scale },
  box: { label: 'Box', Icon: Inventory2 },
  lock: { label: 'Hold', Icon: Lock },
  category: { label: 'Category', Icon: Category },
  money: { label: 'Money', Icon: MonetizationOn },
  clock: { label: 'Clock', Icon: Schedule },
  calendar: { label: 'Calendar', Icon: Event },
  moon: { label: 'Moon', Icon: Nightlight },
  crescent: { label: 'Crescent', Icon: Brightness2 },
  restaurant: { label: 'Restaurant', Icon: Restaurant },
  cake: { label: 'Cake', Icon: Cake },
  science: { label: 'Lab', Icon: Science },
  school: { label: 'Training', Icon: School },
  tools: { label: 'Tools', Icon: Handyman },
  hardware: { label: 'Hardware', Icon: Hardware },
  construction: { label: 'Maintenance', Icon: Construction },
  waste: { label: 'Disposal', Icon: DeleteOutline },
} as const satisfies Record<string, { label: string; Icon: SvgIconComponent }>;

export type JobTagIconKey = keyof typeof JOB_TAG_ICONS;

/**
 * Rendered for a tag whose stored key isn't in the registry — a key retired
 * from the set, or a row written by an older build. Must match
 * `DEFAULT_JOB_TAG_ICON` on the API's `JobTag` model.
 */
export const DEFAULT_JOB_TAG_ICON: JobTagIconKey = 'label';

export const JOB_TAG_ICON_KEYS = Object.keys(JOB_TAG_ICONS) as JobTagIconKey[];

/**
 * Resolves a stored icon key to its component, never throwing: an unknown key
 * falls back to the default rather than crashing a calendar paint.
 * @param key the icon key stored on the tag.
 * @returns the icon component to render.
 */
export function resolveJobTagIcon(key: string | null | undefined) {
  return (
    JOB_TAG_ICONS[key as JobTagIconKey] ?? JOB_TAG_ICONS[DEFAULT_JOB_TAG_ICON]
  ).Icon;
}
